/**
 * SeatRouteGuard — redirects any seat away from surfaces it must not access.
 *
 * Replaces the attorney-only AttorneyStaffRouteGuard with a unified policy
 * map (seatRoutePolicy.ts) that covers attorney, data_specialist,
 * accountant_internal, and future seats.
 *
 * Role resolution:
 *   1. Backend admin/owner → NEVER blocked (overrides everything).
 *   2. Workspace role 'attorney' → attorney policy.
 *   3. Backend data_specialist / accountant → data_specialist / accountant_internal.
 *   4. Otherwise fall back to the workspace role.
 *
 * While the role is unresolved (workspaceRole null or permissions still
 * loading) the guard renders null so no staff page mounts and fires fetches.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useWorkspaceRole } from '../../hooks/useWorkspaceRole';
import { usePermissionsOptional } from '../../contexts/PermissionsContext';
import {
  SEAT_ROUTE_POLICY,
  isPathBlocked,
  type SeatRole,
} from './seatRoutePolicy';

export function resolveEffectiveRole(
  workspaceRole: SeatRole | null,
  backendRole: string | null,
): SeatRole | null {
  if (backendRole === 'admin' || backendRole === 'owner') {
    return 'admin';
  }
  if (workspaceRole === 'attorney') {
    return 'attorney';
  }
  if (backendRole === 'data_specialist') {
    return 'data_specialist';
  }
  if (backendRole === 'accountant') {
    return 'accountant_internal';
  }
  return workspaceRole;
}

export function SeatRouteGuard({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement | null {
  const location = useLocation();
  const workspaceRole = useWorkspaceRole();
  const permissions = usePermissionsOptional();
  const backendRole = permissions?.role ?? null;
  const permissionsLoading = permissions?.loading ?? false;

  // Unresolved — don't mount the page (staff pages would fire fetches), but show the
  // same branded spinner AuthTokenRegistrar uses so there's no blank flash (K3 R1).
  if (workspaceRole === null || permissionsLoading) {
    return (
      <div
        data-testid="seat-route-guard-loading"
        role="status"
        aria-label="Loading"
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}
      >
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--color-brand)' }} />
      </div>
    );
  }

  const effectiveRole = resolveEffectiveRole(workspaceRole, backendRole);

  if (effectiveRole && isPathBlocked(effectiveRole, location.pathname)) {
    const home = SEAT_ROUTE_POLICY[effectiveRole]?.home ?? '/dashboard';
    return <Navigate to={home} replace />;
  }

  return <>{children}</>;
}

/** Backward-compatible alias — existing imports keep working. */
